import type { CSSProperties } from 'react'
import { Stack, Group, Badge, Text, Table, Paper, Loader, Center, Alert, Tooltip } from '@mantine/core'
import { IconAlertCircle, IconShieldCheck } from '@tabler/icons-react'
import type { UserLoginActivityResponse } from '../../api/loginActivityApi'
import { FLAG_LABELS, SEVERITY_COLORS, formatLocation } from '../../utils/loginAnomaly'

const NOWRAP = { whiteSpace: 'nowrap' as const }
const ELLIPSIS = { maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }

const METHOD_LABELS: Record<string, string> = {
  password: 'Password',
  passkey: 'Passkey',
  magic_link: 'Magic link',
  handoff: 'Handoff',
}

const METHOD_COLORS: Record<string, string> = {
  password: 'gray',
  passkey: 'blue',
  magic_link: 'grape',
  handoff: 'cyan',
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function TruncatedText({ text, style }: { text: string; style?: CSSProperties }) {
  return (
    <Tooltip label={text} disabled={text.length < 20} openDelay={300}>
      <Text size="sm" style={{ ...ELLIPSIS, ...style }}>{text}</Text>
    </Tooltip>
  )
}

interface LoginActivityPanelProps {
  activity: UserLoginActivityResponse | null
  loading: boolean
  error: string | null
}

export function LoginActivityPanel({ activity, loading, error }: LoginActivityPanelProps) {
  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    )
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        {error}
      </Alert>
    )
  }

  if (!activity) {
    return null
  }

  return (
    <Stack gap="md">
      {activity.flags.length === 0 ? (
        <Group gap="xs" c="dimmed">
          <IconShieldCheck size={18} />
          <Text size="sm">No anomalies detected in the last {activity.windowDays} days</Text>
        </Group>
      ) : (
        <Group gap="xs">
          {activity.flags.map((f) => (
            <Tooltip key={f.flag} label={f.detail} multiline w={260}>
              <Badge color={SEVERITY_COLORS[f.severity]} variant="light">
                {FLAG_LABELS[f.flag]}
              </Badge>
            </Tooltip>
          ))}
        </Group>
      )}

      <Group gap="xl">
        <div>
          <Text size="sm" c="dimmed" mb={2}>Distinct IPs</Text>
          <Text fw={500}>{activity.summary.distinctIpCount}</Text>
        </div>
        <div>
          <Text size="sm" c="dimmed" mb={2}>Distinct Networks</Text>
          <Text fw={500}>{activity.summary.distinctAsnCount}</Text>
        </div>
        <div>
          <Text size="sm" c="dimmed" mb={2}>Distinct Countries</Text>
          <Text fw={500}>{activity.summary.distinctCountryCount}</Text>
        </div>
        <div>
          <Text size="sm" c="dimmed" mb={2}>Apps</Text>
          <Text fw={500}>{activity.summary.apps.join(', ') || '—'}</Text>
        </div>
      </Group>

      {activity.events.length === 0 ? (
        <Text c="dimmed" size="sm">No login activity in the last {activity.windowDays} days</Text>
      ) : (
        <Paper withBorder style={{ overflowX: 'auto' }}>
          <Table verticalSpacing="xs" horizontalSpacing="sm" miw={780}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={NOWRAP}>Time</Table.Th>
                <Table.Th style={NOWRAP}>App</Table.Th>
                <Table.Th style={NOWRAP}>IP</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>Network</Table.Th>
                <Table.Th style={NOWRAP}>Method</Table.Th>
                <Table.Th style={NOWRAP}>Reputation</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[...activity.events].reverse().map((event, i) => (
                <Table.Tr key={`${event.timestamp}-${event.ip}-${i}`}>
                  <Table.Td style={NOWRAP}>
                    <Text size="sm">{formatTimestamp(event.timestamp)}</Text>
                    {event.action === 'session_ip_change' && (
                      <Text size="xs" c="dimmed">mid-session change</Text>
                    )}
                  </Table.Td>
                  <Table.Td style={NOWRAP}>
                    <Text size="sm">{event.appId}</Text>
                  </Table.Td>
                  <Table.Td style={NOWRAP}>
                    <Text size="sm" ff="monospace">{event.ip}</Text>
                  </Table.Td>
                  <Table.Td>
                    <TruncatedText text={formatLocation(event.geo)} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <TruncatedText text={event.geo?.asOrg ?? '—'} />
                      {event.geo?.isDatacenter && (
                        <Badge size="xs" variant="light" color="gray" style={NOWRAP}>datacenter</Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td style={NOWRAP}>
                    {event.method ? (
                      <Badge color={METHOD_COLORS[event.method] ?? 'gray'} variant="light" size="sm">
                        {METHOD_LABELS[event.method] ?? event.method}
                      </Badge>
                    ) : (
                      <Text size="sm" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td style={NOWRAP}>
                    {event.reputation ? (
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label={event.reputation.usageType ?? 'No usage type on file'} openDelay={300}>
                          <Badge
                            size="sm"
                            variant="light"
                            color={event.reputation.score >= 50 ? 'red' : event.reputation.score >= 20 ? 'yellow' : 'gray'}
                          >
                            {event.reputation.score}
                          </Badge>
                        </Tooltip>
                        {event.reputation.isTor && (
                          <Badge size="xs" variant="light" color="violet">Tor</Badge>
                        )}
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  )
}
